import React from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../../shared/services/apiService.js';

// Logos
import pic1 from '../assets/images/company-logo/pic1.png';
import pic2 from '../assets/images/company-logo/pic2.png';
import pic3 from '../assets/images/company-logo/pic3.png';
import pic4 from '../assets/images/company-logo/pic4.png';
import pic5 from '../assets/images/company-logo/pic5.png';
import pic6 from '../assets/images/company-logo/pic6.png';

const STATIC_COMPANIES = [
  { image: pic1, name: 'DGI' },
  { image: pic2, name: 'CICES' },
  { image: pic3, name: 'PNDL' },
  { image: pic4, name: 'ANACIM' },
  { image: pic5, name: 'SWEDD' },
  { image: pic6, name: 'ASPT' },
];

const FeaturedCompanies = () => {
  const [titleHtml, setTitleHtml] = React.useState("<span style='font-weight: bold;'>Sociétés</span> en vedette");
  const [seeMoreLink, setSeeMoreLink] = React.useState('#');
  const [companies, setCompanies] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [useStaticFallback, setUseStaticFallback] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;
    let abortController = new AbortController();
    
    (async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('[FeaturedCompanies] Calling API...');
        
        // Use the correct homeSections endpoint
        const res = await apiClient.get('/homeSections/getCompanies', {
          params: { 
            fetchData: 1
          },
          headers: { 
            'X-Skip-Auth': true
          },
          signal: abortController.signal
        });
        
        console.log('[FeaturedCompanies] API Response:', res?.data);
        
        const root = res?.data;
        // Parse homeSections response format
        const featured = root?.result?.data?.featuredCompanies || {};
        const list = Array.isArray(featured.companies) ? featured.companies : [];
        const maxItems = Number(root?.result?.getCompaniesOp?.max_items || 12);
        
        console.log('[FeaturedCompanies] Featured companies from API:', list);
        
        const mapped = list.map((c) => {
          // Handle logo URL properly, converting https://localhost to http://localhost
          let logoUrl = pic1; // default fallback
          if (c.logo_url) {
            logoUrl = c.logo_url.small || c.logo_url.medium || c.logo_url.full;
            // Fix localhost SSL issues
            if (logoUrl && logoUrl.includes('https://localhost:8000')) {
              logoUrl = logoUrl.replace('https://localhost:8000', 'http://localhost:8000');
            }
          } else if (c.logo) {
            logoUrl = `http://localhost:8000/storage/${c.logo}`;
          }
          
          return {
            id: c.id,
            name: c.name,
            image: logoUrl,
            posts_count: c.posts_count || 0,
          };
        });
        
        if (!mounted) return;
        
        // Only use API data if we have actual companies
        if (mapped.length > 0) {
          setTitleHtml(featured.title || titleHtml);
          setSeeMoreLink(featured.link || '#');
          setCompanies(mapped.slice(0, maxItems));
          setUseStaticFallback(false);
          console.log('[FeaturedCompanies] Using API data:', mapped);
        } else {
          // No companies from API, use static fallback
          console.log('[FeaturedCompanies] No companies from API, using static fallback');
          setCompanies(STATIC_COMPANIES);
          setUseStaticFallback(true);
        }
      } catch (err) {
        if (err.name === 'AbortError') {
          console.log('[FeaturedCompanies] Request was aborted');
          return;
        }
        console.error('[FeaturedCompanies] API Error:', err);
        if (!mounted) return;
        setError(err.message || 'Erreur lors du chargement des sociétés');
        // Only use static fallback on actual error
        setCompanies(STATIC_COMPANIES);
        setUseStaticFallback(true);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    
    return () => { 
      mounted = false; 
      abortController.abort();
    };
  }, []);

  return (
    <section className="modern-featured-companies">
      <div className="container">
        {/* Modern Header */}
        <div className="modern-section-header">
          <div className="section-badge">
            <i className="fa-solid fa-building"></i>
            <span>Partenaires</span>
          </div>
          <h2 className="section-title" dangerouslySetInnerHTML={{ __html: titleHtml }}></h2>
          <p className="section-subtitle">
            Découvrez les entreprises leaders qui font confiance à notre plateforme
          </p>
          <div className="section-status">
            {useStaticFallback && (
              <span className="status-badge warning">
                <i className="fa-solid fa-exclamation-triangle"></i>
                Données statiques
              </span>
            )}
            {!useStaticFallback && companies.length > 0 && (
              <span className="status-badge success">
                <i className="fa-solid fa-check-circle"></i>
                Données en temps réel
              </span>
            )}
          </div>
        </div>

        {loading && (
          <div className="modern-loading">
            <div className="loading-spinner"></div>
            <p>Chargement des sociétés partenaires...</p>
          </div>
        )}

        {error && !loading && (
          <div className="modern-alert">
            <i className="fa-solid fa-exclamation-triangle"></i>
            <div>
              <strong>Information:</strong> {error}. Affichage des données de démonstration.
            </div>
          </div>
        )}

        {!loading && (
          <div className="companies-grid">
            {companies.map((c, idx) => (
              <Link 
                key={c.id ?? idx} 
                to={c.id ? `/companies/${c.id}/posts` : '#'} 
                className="company-card"
              >
                <div className="company-card-inner">
                  <div className="company-logo-container">
                    <div className="company-logo">
                      <img
                        src={c.image}
                        alt={c.name}
                        onError={(e) => {
                          e.target.src = pic1;
                        }}
                      />
                    </div>
                    <div className="logo-glow"></div>
                  </div>
                  
                  <div className="company-info">
                    <h3 className="company-name">{c.name}</h3>
                    {typeof c.posts_count === 'number' && c.posts_count > 0 && (
                      <div className="company-stats">
                        <span className="job-count">
                          <i className="fa-solid fa-briefcase"></i>
                          {c.posts_count} offre{c.posts_count > 1 ? 's' : ''}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="company-card-footer">
                    <span className="view-jobs-btn">
                      Voir les offres
                      <i className="fa-solid fa-arrow-right"></i>
                    </span>
                  </div>
                  
                  <div className="card-hover-effect"></div>
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="section-footer">
          <a href={seeMoreLink} className="view-all-companies-btn">
            <span>Voir toutes les entreprises</span>
            <i className="fa-solid fa-arrow-right"></i>
          </a>
        </div>
      </div>

      <style jsx>{`
        .modern-featured-companies {
          padding: 80px 0;
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          position: relative;
          overflow: hidden;
        }

        .modern-featured-companies::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><g fill="%23e2e8f0" fill-opacity="0.3"><circle cx="30" cy="30" r="1"/></g></svg>') repeat;
          opacity: 0.5;
        }

        .modern-section-header {
          text-align: center;
          margin-bottom: 60px;
          position: relative;
          z-index: 1;
        }

        .section-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
          padding: 8px 20px;
          border-radius: 50px;
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 20px;
          box-shadow: 0 4px 20px rgba(16, 185, 129, 0.3);
        }

        .section-title {
          font-size: 2.5rem;
          font-weight: 800;
          color: #1e293b;
          margin-bottom: 16px;
          line-height: 1.2;
        }

        .section-subtitle {
          font-size: 1.1rem;
          color: #64748b;
          max-width: 600px;
          margin: 0 auto 20px;
          line-height: 1.6;
        }

        .section-status {
          display: flex;
          justify-content: center;
          gap: 12px;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 16px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
        }

        .status-badge.success {
          background: rgba(16, 185, 129, 0.1);
          color: #059669;
          border: 1px solid rgba(16, 185, 129, 0.2);
        }

        .status-badge.warning {
          background: rgba(245, 158, 11, 0.1);
          color: #d97706;
          border: 1px solid rgba(245, 158, 11, 0.2);
        }

        .modern-loading {
          text-align: center;
          padding: 60px 20px;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #e2e8f0;
          border-top: 3px solid #10b981;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .modern-alert {
          display: flex;
          align-items: center;
          gap: 12px;
          background: rgba(245, 158, 11, 0.1);
          border: 1px solid rgba(245, 158, 11, 0.2);
          border-radius: 12px;
          padding: 16px 20px;
          margin-bottom: 40px;
          color: #d97706;
        }

        .companies-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 24px;
          margin-bottom: 60px;
        }

        .company-card {
          text-decoration: none;
          color: inherit;
          display: block;
          height: 100%;
        }

        .company-card-inner {
          background: white;
          border-radius: 20px;
          padding: 32px 24px;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          position: relative;
          overflow: hidden;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border: 1px solid rgba(226, 232, 240, 0.8);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }

        .company-card:hover .company-card-inner {
          transform: translateY(-8px);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          border-color: rgba(16, 185, 129, 0.3);
        }

        .company-logo-container {
          position: relative;
          margin-bottom: 6px;
          width: 100%;
          display: flex;
          justify-content: center;
        }

        .company-logo {
          width: 96px;
          height: 96px;
          border-radius: 16px;
          background: #f8fafc;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          border: 2px solid #e2e8f0;
          transition: all 0.3s ease;
        }

        .company-card:hover .company-logo {
          border-color: #10b981;
          transform: scale(1.05);
        }

        .company-logo img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          padding: 8px;
        }

        .logo-glow {
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(16, 185, 129, 0.1) 0%, transparent 70%);
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .company-card:hover .logo-glow {
          opacity: 1;
        }

        .company-info {
          flex: 1;
          margin-bottom: 4px;
        }

        .company-name {
          font-size: 1.2rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 2px;
          line-height: 1.3;
        }

        .company-stats {
          display: flex;
          justify-content: center;
          gap: 8px;
        }

        .job-count {
          display: flex;
          align-items: center;
          gap: 6px;
          background: rgba(16, 185, 129, 0.1);
          color: #059669;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 600;
        }

        .company-card-footer {
          margin-top: 4px;
        }

        .view-jobs-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: #10b981;
          font-weight: 600;
          font-size: 14px;
          transition: all 0.3s ease;
        }

        .company-card:hover .view-jobs-btn {
          color: #059669;
          transform: translateX(4px);
        }

        .card-hover-effect {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 150, 105, 0.05) 100%);
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .company-card:hover .card-hover-effect {
          opacity: 1;
        }

        .section-footer {
          text-align: center;
        }

        .view-all-companies-btn {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
          padding: 16px 32px;
          border-radius: 50px;
          text-decoration: none;
          font-weight: 600;
          font-size: 16px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 4px 20px rgba(16, 185, 129, 0.3);
        }

        .view-all-companies-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(16, 185, 129, 0.4);
          color: white;
        }

        @media (max-width: 768px) {
          .modern-featured-companies {
            padding: 60px 0;
          }

          .section-title {
            font-size: 2rem;
          }

          .companies-grid {
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 20px;
          }

          .company-card-inner {
            padding: 18px 16px;
          }

          .company-logo-container {
            margin-bottom: 8px;
          }

          .company-logo {
            width: clamp(80px, 26vw, 96px);
            height: clamp(80px, 26vw, 96px);
          }

          .company-info {
            margin-bottom: 4px;
          }

          .company-name {
            font-size: 1.05rem;
            margin-bottom: 2px;
          }
        }
      `}</style>
    </section>
  );
};

export default FeaturedCompanies;
